import type { Dayjs } from "dayjs";

import type {
  Transaction,
  TransactionStatus,
  TransactionTotals,
} from "../../../../types/transactions";

export type PaymentDirection = "deposit" | "withdrawal";

export type PaymentRow = Transaction & {
  payment_direction: PaymentDirection;
};

const STATUSES: Array<Exclude<TransactionStatus, null | undefined>> = [
  "pending",
  "approved",
  "declined",
];

const GATEWAYS = ["Finix", "Nuvei", "Bitso"] as const;
const PAYMENT_TYPES = ["Card", "ACH", "CLABE"] as const;
const CARD_BANK_INFO = ["**** 4242", "**** 1111", "CLABE ****9012"] as const;

const buildRow = (
  index: number,
  now: Dayjs,
  direction: PaymentDirection,
  amount: number,
  balance: number,
  extras: Partial<PaymentRow> = {},
): PaymentRow => {
  const isDeposit = direction === "deposit";
  const status = STATUSES[index % STATUSES.length];
  const gateway = GATEWAYS[index % GATEWAYS.length];
  const paymentType = PAYMENT_TYPES[index % PAYMENT_TYPES.length];

  return {
    transaction_id: `pay-${String(index + 1).padStart(3, "0")}`,
    timestamp: now.toISOString(),
    wallet_type: "Cash",
    amount: amount.toFixed(2),
    transaction_type: isDeposit ? "Deposit" : "Withdrawal",
    title: isDeposit ? "Cash Deposit" : "Cash Withdraw",
    payment_direction: direction,
    by_admin: index % 7 === 0,
    reason: index % 7 === 0 ? "Manual adjustment" : null,
    total_balance: balance.toFixed(2),
    status,
    payment_gateway: gateway,
    payment_type: paymentType,
    card_bank_info: CARD_BANK_INFO[index % CARD_BANK_INFO.length],
    ...extras,
  };
};

/**
 * Mock payment activity for the User Manager Payments tab.
 * Replace with the v2 payments endpoint once the API ships.
 */
export const buildMockPayments = (now: Dayjs): PaymentRow[] => {
  const rows: PaymentRow[] = [];
  let balance = 2500;

  for (let index = 0; index < 24; index += 1) {
    const isDeposit = index % 3 !== 2;
    const amount = isDeposit ? 50 + index * 15 : 40 + index * 10;
    if (isDeposit) {
      balance += amount;
    } else {
      balance = Math.max(0, balance - amount);
    }

    rows.push(
      buildRow(
        index,
        now.startOf("day").add(index * 30, "minute"),
        isDeposit ? "deposit" : "withdrawal",
        amount,
        balance,
        index === 1
          ? {
              payment_gateway: null,
              payment_type: null,
              status: null,
              card_bank_info: "",
            }
          : index === 5
          ? {
              is_verification: true,
              total_balance: null,
              verification_details: {
                clabe: "646180110400000007",
                holder_name: "Test User",
                verify_wid: "wid-1",
                reason: "Penny verification",
                aml_flag: true,
                created_at: now.toISOString(),
                updated_at: now.toISOString(),
              },
            }
          : {},
      ),
    );
  }

  return rows;
};

export const computeMockPaymentTotals = (
  payments: PaymentRow[],
): TransactionTotals => {
  return payments.reduce<TransactionTotals>(
    (totals, payment) => {
      const amount = Number(payment.amount ?? 0);
      if (payment.payment_direction === "deposit") {
        totals.total_purchased += amount;
      } else {
        totals.total_withdrawn += amount;
      }
      return totals;
    },
    { total_purchased: 0, total_withdrawn: 0 },
  );
};
